// src/components/atomic-crm/profile/ProfileCard.tsx
import { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ds/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ds/ui/card";
import { Button } from "@/components/ds/ui/button";
import {
  RefreshCw,
  Sparkles,
  Loader2,
  Briefcase,
  Lightbulb,
  MessagesSquare,
  Newspaper,
} from "lucide-react";
import type { AIProfile } from "@/types/ai-profile";
import { formatDistanceToNow } from "date-fns";

interface ProfileCardProps {
  profileRow: {
    profile: AIProfile;
    generated_at: string;
    model?: string | null;
    regenerated_count?: number;
  } | null;
  variant?: "contact" | "company";
  isGenerating: boolean;
  onGenerate: () => void;
  onRefresh: () => void;
}

export function ProfileCard({
  profileRow,
  variant = "contact",
  isGenerating,
  onGenerate,
  onRefresh,
}: ProfileCardProps) {
  const [activeTab, setActiveTab] = useState("brief");

  // ── Empty state ──────────────────────────────────────────────────────────
  if (!profileRow) {
    return (
      <Card className="w-full border border-dashed shadow-sm">
        <CardContent className="py-10 flex flex-col items-center justify-center gap-3 text-center">
          <Sparkles className="h-6 w-6 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            No profile generated yet.
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onGenerate}
            disabled={isGenerating}
            className="gap-2"
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {isGenerating ? "Generating…" : "Generate profile"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const profile = profileRow.profile;
  const lastGenerated = profileRow.generated_at;

  // ── Populated state ─────────────────────────────────────────────────────
  const hasNews = (profile.recent_news?.length ?? 0) > 0;
  const hasCareer =
    variant === "contact" &&
    ((profile.career_history?.length ?? 0) > 0 || !!profile.role_context);

  const tabs: Array<{ key: string; label: string; icon: React.ReactNode }> = [
    {
      key: "brief",
      label: "Brief",
      icon: <Briefcase className="h-3.5 w-3.5" />,
    },
    {
      key: "market",
      label: "Market",
      icon: <Lightbulb className="h-3.5 w-3.5" />,
    },
    {
      key: "insights",
      label: "Insights",
      icon: <MessagesSquare className="h-3.5 w-3.5" />,
    },
    {
      key: "questions",
      label: "Questions",
      icon: <MessagesSquare className="h-3.5 w-3.5" />,
    },
  ];
  if (hasNews) {
    tabs.push({
      key: "news",
      label: "News",
      icon: <Newspaper className="h-3.5 w-3.5" />,
    });
  }
  if (hasCareer) {
    tabs.push({
      key: "career",
      label: "Career",
      icon: <Briefcase className="h-3.5 w-3.5" />,
    });
  }

  const gridCols =
    tabs.length >= 6
      ? "grid-cols-6"
      : tabs.length === 5
        ? "grid-cols-5"
        : "grid-cols-4";

  return (
    <Card className="w-full mt-4 border shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Profile</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isGenerating}
            className="h-8 gap-1.5"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isGenerating ? "animate-spin" : ""}`}
            />
            {isGenerating ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pb-3">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${gridCols}`}>
            {tabs.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="flex items-center gap-1.5"
              >
                {t.icon}
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent
            value="brief"
            className="mt-3 space-y-3 text-sm leading-relaxed"
          >
            <div>{profile.brief || "No brief available."}</div>
            {profile.role_context && (
              <div className="text-muted-foreground">
                <span className="font-medium text-foreground">
                  Role context:{" "}
                </span>
                {profile.role_context}
              </div>
            )}
            {profile.background && (
              <div className="text-muted-foreground">
                <span className="font-medium text-foreground">
                  Background:{" "}
                </span>
                {profile.background}
              </div>
            )}
          </TabsContent>

          <TabsContent value="market" className="mt-3 text-sm leading-relaxed">
            {profile.market_intelligence?.length ? (
              <ul className="list-disc list-inside space-y-1">
                {profile.market_intelligence.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : (
              "No market intelligence available."
            )}
          </TabsContent>

          <TabsContent
            value="insights"
            className="mt-3 space-y-3 text-sm leading-relaxed"
          >
            {profile.key_insights?.length ? (
              <ul className="list-disc list-inside space-y-1">
                {profile.key_insights.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : (
              "No key insights available."
            )}
            {profile.competitors?.length ? (
              <div>
                <span className="font-medium text-foreground">
                  Competitors:{" "}
                </span>
                {profile.competitors.join(", ")}
              </div>
            ) : null}
          </TabsContent>

          <TabsContent
            value="questions"
            className="mt-3 text-sm leading-relaxed"
          >
            {profile.suggested_questions?.length ? (
              <ul className="list-disc list-inside space-y-1">
                {profile.suggested_questions.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : (
              "No suggested questions available."
            )}
          </TabsContent>

          {hasNews && (
            <TabsContent value="news" className="mt-3 text-sm leading-relaxed">
              <ul className="space-y-2">
                {profile.recent_news?.map((item: any, i: number) => (
                  <li key={i}>
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {item.title}
                      </a>
                    ) : (
                      item.title
                    )}
                  </li>
                ))}
                {profile.recent_news?.map((item: any, i: number) => (
                  <li key={i}>
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {item.title}
                      </a>
                    ) : (
                      item.title
                    )}
                  </li>
                ))}
              </ul>
            </TabsContent>
          )}

          {hasCareer && (
            <TabsContent
              value="career"
              className="mt-3 text-sm leading-relaxed"
            >
              {profile.career_history?.length ? (
                <ul className="list-disc list-inside space-y-1">
                  {profile.career_history.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                "No career history available."
              )}
            </TabsContent>
          )}
        </Tabs>
      </CardContent>

      <CardFooter className="border-t pt-3 flex justify-between items-center">
        <div className="text-xs text-muted-foreground">
          Last updated{" "}
          {formatDistanceToNow(new Date(lastGenerated), { addSuffix: true })}
        </div>
        {profile._model && (
          <div className="text-[10px] text-muted-foreground/60">
            {profile._model} · {profile._prompt_version}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
